import { DATE_YMD_HMS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { ChangeType, HistoryChangeUser } from '@/types/account';
import { formatDateTime } from '@/utils/datetime';
import { isPathEk } from '@/utils/routes';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface Props {
    history: HistoryChangeUser
}

export default observer(function HistoryItem({ history }: Props) {
    // hook
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const isEk = isPathEk(pathname);

    // store
    const {
        generalStore: { licenseTypeGas, licenseTypeEk }
    } = useStore();

    // state
    const changesArr = useMemo(() => {
        const changes = toJS(history?.changes ?? []);
        const array = Object.entries(changes).map(([key, value]) => ({
            key: `${key}`,
            new: value?.new,
            old: value?.old,
            initValue: value
        }));

        return array;
    }, [history?.changes]);

    // function
    const getLicenseText = (value: number[]): string => {
        if (value?.length < 1) {
            return '';
        }

        const numberValueLicense = Number(value?.[0]);
        const licenseArr = isEk ? toJS(licenseTypeEk) : toJS(licenseTypeGas);
        const licenseCurrent = licenseArr?.find(item => item?.id === numberValueLicense);

        if (licenseCurrent) {
            return licenseCurrent?.name ?? '';
        }
        return '';
    };

    const convertValueOldNew = (value: ChangeType): string => {
        if (typeof value === 'object' && !Array.isArray(value)) {
            return value?.fileName ? value?.fileName : '';
        } else if (Array.isArray(value)) {
            return getLicenseText(value);
        }

        return value || '';
    };

    const convertValueStatus = (value: number): string => {
        if (isNaN(value)) return '';
        return value ? t('field.active') : t('field.inactive');
    };

    const textRenderChange = (change: {
        key: string,
        old: ChangeType,
        new: ChangeType,
        initValue: any
    }): React.ReactNode => {
        if (typeof change?.initValue === 'boolean' && change?.initValue === true) {
            return (
                <>変更</>
            );
        }

        let oldTmp: string = '', newTmp: string = '';

        if (change?.key === 'status') {
            oldTmp = convertValueStatus(Number(change?.old));
            newTmp = convertValueStatus(Number(change?.new));
        } else {
            oldTmp = convertValueOldNew(change?.old);
            newTmp = convertValueOldNew(change?.new);
        }

        if (oldTmp?.length < 1 && newTmp?.length > 0) {
            return (
                <>
                    <span className='text-green-600'>{newTmp}</span>
                    <span className='whitespace-nowrap'>を追加</span>
                </>
            );
        } else if (oldTmp?.length > 0 && newTmp?.length < 1) {
            return (
                <span className='whitespace-nowrap'>削除</span>
            );
        } else if (oldTmp?.length > 0 && newTmp?.length > 0) {
            return (
                <>
                    <span className='text-red-600 line-through'>{oldTmp}</span>
                    <span className='whitespace-nowrap'>から</span>
                    <span className='text-green-600'>{newTmp}</span>
                    <span className='whitespace-nowrap'>に変更</span>
                </>
            );
        }

        return '';
    };

    return (
        <div>
            <div>
                <div>
                    <span className='font-semibold'>
                        {`${t('field.update_time')}：`}
                    </span>
                    {formatDateTime(history?.updatedAt, DATE_YMD_HMS, { useLocalTime: false })}
                </div>
                <div>
                    <span className='font-semibold'>
                        {`${t('field.update_by')}：`}
                    </span>
                    {history?.changeBy?.userCode || '--'}
                </div>
                <div>
                    <span className='font-semibold'>変更内容：</span>
                    <div className='flex flex-col gap-2 pt-[10px] pl-[30px]'>
                        {changesArr.map(change => change.key === KEY_USER?.[`${change.key}`] && (
                            <div key={change.key} className='break-all'>
                                <span className='whitespace-nowrap'>
                                    {t(USER_INFO[change.key]?.label)}
                                    ：
                                </span>
                                <span>
                                    {textRenderChange(change)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
});

const KEY_USER = {
    roleId: 'roleId',
    email: 'email',
    fullname: 'fullname',
    furiganaName: 'furiganaName',
    zipCode: 'zipCode',
    address: 'address',
    status: 'status',
    birthOfDate: 'birthOfDate',
    phone: 'phone',
    licenseExporeDate: 'licenseExporeDate',
    licenseNumber: 'licenseNumber',
    licenseType: 'licenseType',
    licenseBeforeImage: 'licenseBeforeImage',
    licenseAfterImage: 'licenseAfterImage',
    minorDriverLicense: 'minorDriverLicense',
    avartarImage: 'avartarImage',
    note: 'note',
    password: 'password'
};

const USER_INFO = {
    [KEY_USER.roleId]: {
        key: KEY_USER.roleId,
        label: 'field.role'
    },
    [KEY_USER.email]: {
        key: KEY_USER.email,
        label: 'field.email'
    },
    [KEY_USER.fullname]: {
        key: KEY_USER.fullname,
        label: 'field.name_or_company_name'
    },
    [KEY_USER.furiganaName]: {
        key: KEY_USER.furiganaName,
        label: 'field.furigana_name'
    },
    [KEY_USER.zipCode]: {
        key: KEY_USER.zipCode,
        label: 'field.post_code'
    },
    [KEY_USER.address]: {
        key: KEY_USER.address,
        label: 'field.address'
    },
    [KEY_USER.status]: {
        key: KEY_USER.status,
        label: 'field.status'
    },
    [KEY_USER.birthOfDate]: {
        key: KEY_USER.birthOfDate,
        label: 'field.date_of_birth'
    },
    [KEY_USER.phone]: {
        key: KEY_USER.phone,
        label: 'field.phone_number'
    },
    [KEY_USER.licenseExporeDate]: {
        key: KEY_USER.licenseExporeDate,
        label: 'field.license_expire_date'
    },
    [KEY_USER.licenseNumber]: {
        key: KEY_USER.licenseNumber,
        label: 'field.driver_license_number'
    },
    [KEY_USER.licenseType]: {
        key: KEY_USER.licenseType,
        label: 'field.driver_license_type'
    },
    [KEY_USER.licenseBeforeImage]: {
        key: KEY_USER.licenseBeforeImage,
        label: 'field.license_before_image'
    },
    [KEY_USER.licenseAfterImage]: {
        key: KEY_USER.licenseAfterImage,
        label: 'field.license_after_image'
    },
    [KEY_USER.minorDriverLicense]: {
        key: KEY_USER.minorDriverLicense,
        label: 'field.myna_license'
    },
    [KEY_USER.avartarImage]: {
        key: KEY_USER.avartarImage,
        label: 'field.avatar_image'
    },
    [KEY_USER.note]: {
        key: KEY_USER.note,
        label: 'field.note'
    },
    [KEY_USER.password]: {
        key: KEY_USER.password,
        label: 'field.password'
    }
};
